import { Component } from '@angular/core';
import { LoadHandoverService } from '../shared/load-handover.service';
import { LoaderService } from '../../shared/services/loader.service';
import { LoadHandoverGroup } from '../shared/load-handover-group.model';
import { Carrier } from '../../user-dashboard/shared/carrier.model';
import { CarrierService } from '../../user-dashboard/shared/carrier.service';
import { Observable, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { NavigationService } from '../../shared/services/navigation.service';
import { LoadHandover } from '../shared/load-handover.model';
import { LOCAL_STORAGE_KEYS } from '../../core/constants';
import { TripService } from '../../realtime-tracking/shared/trip.service';
import { Trip } from '../../realtime-tracking/shared/trip.model';
import { catchError, finalize, tap } from 'rxjs/operators';
// @ts-ignore
import {} from 'googlemaps';
import { GeocodingService } from '../../shared/services/geocoding.service';
import { Latng } from '../../shared/latng';
import { NavigationLinkService } from '../../shared/services/navigation-link.service';
import { UserProfile } from '../../shared/user-profile';

@Component({
  selector: 'app-load-handover-map',
  templateUrl: './load-handover-map.component.html',
  styleUrls: ['./load-handover-map.component.scss']
})
export class LoadHandoverMapComponent {

  carriers: Carrier[] = [];

  loadHandoverGroups: LoadHandoverGroup[] = [];

  loadHandovers: LoadHandover[]         = [];
  oldLoadHandovers: LoadHandover[]      = [];
  filteredLoadHandovers: LoadHandover[] = [];

  canViewOnLoadTracking          = true;
  selectedHandover: LoadHandover = null;
  selectedHandoverAddress        = null;

  defaultLat  = 47.988212705125434;
  defaultLng  = 13.40030886232853;
  defaultZoom = 5;

  lat  = this.defaultLat;
  lng  = this.defaultLng;
  zoom = this.defaultZoom;

  selectedCarrier: Carrier  = null;
  carrierSelectionAvailable = false;

  filterHandovers      = false;
  showOnlyOldHandover  = false;
  filterHandoversInput = null;

  selectedReleasedDropIconUrl = '/assets/marker-release-red.png';
  oldReleasedDropIconUrl      = '/assets/marker-release-blue.png';
  releasedDropIconUrl         = '/assets/marker-release-grey.png';
  oldReleasedMapDropIconUrl   = '/assets/marker-release-map-blue.png';
  releasedDropMapIconUrl      = '/assets/marker-release-map-grey.png';
  selectedDropMapIconUrl      = '/assets/marker-release-map-red.png';

  map;

  constructor(
    private authService: AuthenticationService,
    private loadHandoverService: LoadHandoverService,
    private carrierService: CarrierService,
    private geocodingService: GeocodingService,
    private translate: TranslateService,
    private loader: LoaderService,
    private toastr: ToastrService,
    private navigation: NavigationService,
    public link: NavigationLinkService,
    private tripService: TripService
  ) {
    if (this.authService.canReadAllLoadHandovers()) {
      this.carrierSelectionAvailable = true;
      this.fetchCarriers().subscribe(() => {
        this.fetchLoadHandoversForSelectedCarrier();
      });
    } else {
      const savedFeatureProfile = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.PROFILE)) as UserProfile;
      this.canViewOnLoadTracking = savedFeatureProfile ? savedFeatureProfile?.featureProfile?.loadTracking : false;
      this.fetchLoadHandoversForSelfCarrier();
    }
  }

  mapReady(e) {
    this.map = e;
  }

  fetchCarriers(): Observable<Carrier[]> {
    this.loader.startLoad(false);

    return this.carrierService.getAll()
      .pipe(
        finalize(() => this.loader.stopLoad()),
        tap(carriers => this.carriers = carriers),
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          this.toastr.error(error.error.message || this.translate.instant('load-handovers.map.errors.fetch-carriers'));

          return of([]);
        })
      );
  }

  fetchLoadHandoversForSelfCarrier() {
    this.loadHandoverGroups    = [];
    this.loadHandovers         = [];
    this.oldLoadHandovers      = [];
    this.filteredLoadHandovers = [];

    this.loader.startLoad();

    this.loadHandoverService.getLoadHandoversForSelfCarrier()
      .pipe(finalize(() => {
        this.resetFilter();
        this.loader.stopLoad();
      }))
      .subscribe(groups => {
        this.loadHandoverGroups = groups;

        this.loadHandovers = groups.map(g => g.loadHandovers).reduce((g1, g2) => g1.concat(g2), [])
          .sort((h1, h2) => h2.releasedAt.valueOf() - h1.releasedAt.valueOf());

        this.oldLoadHandovers = this.loadHandovers.filter(l => l.isOld);

        this.applyFilters();
      });
  }

  fetchLoadHandoversForSelectedCarrier() {
    this.loadHandoverGroups    = [];
    this.loadHandovers         = [];
    this.oldLoadHandovers      = [];
    this.filteredLoadHandovers = [];

    if (!this.selectedCarrier) {
      return;
    }

    this.loader.startLoad();

    this.loadHandoverService.getLoadHandoversForCarrier(this.selectedCarrier.id)
      .pipe(finalize(() => {
        this.resetFilter();
        this.loader.stopLoad();
      }))
      .subscribe(groups => {
        this.loadHandoverGroups = groups;

        this.loadHandovers = groups.map(g => g.loadHandovers).reduce((g1, g2) => g1.concat(g2), [])
          .sort((h1, h2) => h2.releasedAt.valueOf() - h1.releasedAt.valueOf());

        this.oldLoadHandovers = this.loadHandovers.filter(l => l.isOld);

        this.applyFilters();
      });
  }

  applyFilters() {
    if (this.showOnlyOldHandover) {
      this.filteredLoadHandovers = this.oldLoadHandovers;
    } else {
      this.filteredLoadHandovers = this.loadHandovers;
    }

    if (this.filterHandoversInput) {
      const filterString = this.filterHandoversInput.toLowerCase();
      const fieldMatches = (field: string) => field && field.toLowerCase().indexOf(filterString) !== -1;

      this.filteredLoadHandovers = this.filteredLoadHandovers.filter(handover => fieldMatches(handover.shipmentNumbers.join(', ')));
    }
  }

  redirectToLoadTracking(loadHandover: LoadHandover, $event) {
    $event.preventDefault();
    let carrierId: string;
    if (this.selectedCarrier) {
      carrierId = this.selectedCarrier.id;
    } else {
      const userProfile = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.PROFILE)) as UserProfile;
      carrierId = userProfile.carrier.id;
    }
    this.navigation.realtimeTracking()
      .overview(carrierId, loadHandover.tripId)
      .go();
  }

  carrierChanged() {
    this.fetchLoadHandoversForSelectedCarrier();
  }

  clickFilterHandovers() {
    this.filterHandovers = !this.filterHandovers;

    if (!this.filterHandovers) {
      this.resetFilter();
    }
  }

  clickEmergencyFilter() {
    this.showOnlyOldHandover = !this.showOnlyOldHandover;

    this.applyFilters();
  }

  resetFilter() {
    this.filterHandoversInput = null;
    this.applyFilters();
  }

  zoomOnHandover(handover: LoadHandover): void {
    const group: LoadHandoverGroup = this.loadHandoverGroups.find(g => g.loadHandovers.includes(handover));

    this.map.panTo({
      lat: group ? group.latitude : handover.releasedLatitude,
      lng: group ? group.longitude : handover.releasedLongitude
    });
    this.map.setZoom(14);
  }

  selectHandover(loadHandover: LoadHandover) {
    if (loadHandover !== this.selectedHandover) {
      this.selectedHandover = loadHandover;
      this.loadHandoverGroups.forEach((handoverGroup) => {
        handoverGroup.hasSelectedLoadHandover = handoverGroup.loadHandovers.includes(loadHandover);
      });
      const selectedGroup                   = this.loadHandoverGroups.find(g => g.loadHandovers.includes(loadHandover));
      selectedGroup.hasSelectedLoadHandover = true;
    }
  }

  getGroupDetails(group: LoadHandoverGroup) {
    if (group.address) {
      this.selectedHandoverAddress = group.address;
      return;
    }

    this.selectedHandoverAddress = null;

    this.loader.startLoad();

    const latng: Latng = { latitude: group.latitude, longitude: group.longitude };
    this.geocodingService.reverseGeocode(latng)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(address => {
        group.address                = address.formatted;
        this.selectedHandoverAddress = address.formatted;
      });

    group.loadHandovers.forEach((handover: LoadHandover) => {
      if (this.authService.canReadAllLoadHandovers()) {
        this.tripService.getByCarrierAndLoadUuid(this.selectedCarrier, handover.loadUuid).subscribe((trip: Trip) => {
          handover.tripId = trip.tripId;
        });
      } else {
        this.tripService.getBySelfCarrierAndLoadUuid(handover.loadUuid).subscribe((trip: Trip) => {
          handover.tripId = trip.tripId;
        });
      }
      console.log(handover);
    });
  }

  getMapMarker(group: LoadHandoverGroup) {
    if (group.hasSelectedLoadHandover) {
      return this.selectedDropMapIconUrl;
    } else if (group.hasAnyOldLoadHandover) {
      return this.oldReleasedMapDropIconUrl;
    } else {
      return this.releasedDropMapIconUrl;
    }
  }

  getListMarker(handover: LoadHandover) {
    if (this.selectedHandover === handover) {
      return this.selectedReleasedDropIconUrl;
    } else if (handover.isOld) {
      return this.oldReleasedDropIconUrl;
    } else {
      return this.releasedDropIconUrl;
    }
  }

}
